import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "~/app/store/types";
import { trackCartLoad } from "~/app/analytics/ga/ga";

import { getData } from "../../api/CheckoutView";
import { type AddressData, type UnloadingPointData } from "~/features/cart/checkout/components/Address";
import { sendData as sendAddressData } from "../../api/CheckoutSelectAddress";
import { getRuntimeConfig } from "~/common/config/runtimeConfig";
import { type RootState } from "~/app/store";
import { orchestrateARefreshOnCheckout } from "./cartRefresh";
import { loadCartPna, quotePackCodeFromEntries } from "~/features/cart/cart/pna/cartPna";
import * as pnaUpdateAccumulator from "../pnaUpdateAccumulator";
import { type CheckoutForm } from "~/features/cart/checkout/components/CheckoutCart";
import { apiSubmitPlaceOrder, ErrorCode } from "../../api/placeOrder";
import { selectCartContainsDigitalServiceTenant } from "../selectors/productEntrySelectors";
import { isProductPMAOnCheckout } from "~/domains/product/utils/productDescriptorUtils";
import { DeliveryOptionItem } from "~/features/cart/checkout/components/DeliveryOption";
import { type TransformedCheckoutData } from "../../transformers/CartResponseTransformer";
import { format } from "~/common/utils/ApiHelper";
import { selectLineItemsByProduct } from "~/features/cart/cart/redux/selectors/cartSelectors";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { AnalyticsMQ } from "~/app/analytics/AnalyticsMQ/AnalyticsMQ";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { LineItem } from "../../models/CartData";
import { SendingType } from "../types";
import { resolveActiveCartResourceId } from "~/domains/cart/utils/cartResourceIdentity";

export const CHECKOUT_LOAD_DATA = "checkoutPage: load cart data";
export const UPDATE_ADDRESS = "checkoutPage: update selected address";
export const UPDATE_UNLOADING_POINT = "checkoutPage: update selected unloading point";
export const SET_IS_SENDING = "checkoutPage: sending data";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export function checkoutPageMounted(): ThunkResult<void> {
  return async (dispatch) => {
    const activeCartId = await resolveActiveCartResourceId();
    const checkoutData = await getData(activeCartId ?? "");

    dispatch(checkoutLoadDataAction(checkoutData));

    const codesToRequest = checkoutData.productCodes;
    if (codesToRequest.length <= getRuntimeConfig().cartPnaThreshold) {
      dispatch(performCartRefresh());
    }
  };
}

export function checkoutLoadDataAction(data: TransformedCheckoutData) {
  return {
    type: CHECKOUT_LOAD_DATA,
    payload: {
      data,
    },
  } as const;
}

export function trackCheckoutLoadThunk(paymentType: string): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const { packageEntriesById } = state.cart;
    const productByCode = state.products.basic;

    trackCartLoad(Object.values(packageEntriesById), productByCode, true, paymentType);
  };
}

function performCartRefresh(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const lineItemsByProduct = selectLineItemsByProduct(state);
    const entries = Object.values(lineItemsByProduct);
    entries.sort((a, b) => (a[0].entryNumber ?? 0) - (b[0].entryNumber ?? 0));

    // Warmup: fire per-product PnA loads without awaiting. Each product's orchestration
    // awaits the SAME loadCartPna calls, and TanStack Query dedupes them into one transport
    // call per unique (productCode, priceReference, packCode) triple.
    for (const lineItems of entries) {
      const productCode = lineItems[0].productCode;
      const base = state.products.basic[productCode];
      const packCode = quotePackCodeFromEntries(lineItems);
      const priceReferences = new Set(lineItems.map((lineItem) => lineItem.priceReference));
      for (const priceReference of priceReferences) {
        void loadCartPna({ productCode, priceReference, base, packCode }).catch(() => undefined);
      }
    }

    const gen = pnaUpdateAccumulator.activate(dispatch);
    entries.forEach((lineItems) => dispatch(orchestrateARefreshOnCheckout(lineItems, gen)));
  };
}

/** placeOrder analytics entry snapshot — provided by the checkout view model (A07). */
export interface PlaceOrderEntrySnapshot {
  lineItem: LineItem;
  product: ProductDescriptor;
}

export function placeOrderThunk(
  form: CheckoutForm,
  isCustomerInitiatedQuote: boolean,
  unloadingPoint: UnloadingPointData | undefined,
  entrySnapshots: PlaceOrderEntrySnapshot[],
): ThunkResult<Promise<ErrorCode | undefined>> {
  return async (dispatch, getState) => {
    dispatch({
      type: SET_IS_SENDING,
      payload: isCustomerInitiatedQuote ? SendingType.QUOTE : SendingType.ORDER,
    });

    const id = (isCustomerInitiatedQuote ? "Q" : "") + format(new Date(), "YYYYDDDDHHmmssSSS");

    const state = getState();
    // PMA derivation from the view-model snapshots (A07 re-home of selectCartHasPMAProducts).
    const hasPMA = entrySnapshots.some(({ product }) => isProductPMAOnCheckout(product));
    const containsDST = selectCartContainsDigitalServiceTenant(state);
    const freightAccountNumber =
      form?.freightPaymentCode !== "" && form?.freightPaymentAccountNumber !== ""
        ? form?.freightPaymentAccountNumber
        : "";

    const data = {
      poNumber: form.poNumber?.trimLeft(),
      orderInstruction: form.orderComment,
      deliveryMethod: form.deliveryCode,
      isCustomerInitiatedQuote: isCustomerInitiatedQuote,
      email: form.optime?.email ?? "",
      firstName: form.optime?.name ?? "",
      lastName: form.optime?.lastName ?? "",
      plant: form.optime?.plant ?? "",
      timeZone: form.optime?.timeZone ?? "",
      language: form.optime?.language ?? "",
      country: form.optime?.country?.toUpperCase() ?? "",
      hasPMA: hasPMA,
      optime: containsDST,
      unloadingPoint: unloadingPoint?.name ?? "",
      completeDelivery: form.completeDelivery === DeliveryOptionItem.COMPLETE,
      paymentType: form.paymentType,
      creditCardID: form?.creditCardID ?? "",
      freightPaymentCode: form?.freightPaymentCode ?? "",
      freightPaymentAccountNumber: freightAccountNumber,
      orderType: form.orderType ?? "",
      trackUid: id,
    };

    try {
      const response = await apiSubmitPlaceOrder(data);

      if (response.success && response.orderCode) {
        AnalyticsMQ.instance.push("placeOrder", {
          orderId: response.orderCode,
          type: isCustomerInitiatedQuote ? "quote" : "purchase",
          paymentType: form.paymentType,
          purchaseValueEur: response.analytics?.purchaseValueEur,
          entries: entrySnapshots,
        });
        redirectToUrl(`/checkout/orderConfirmation/${response.orderCode}`);
        return undefined;
      }
      dispatch({
        type: SET_IS_SENDING,
        payload: false,
      });
      return response.errorCode ?? ErrorCode.Generic;
    } catch {
      dispatch({
        type: SET_IS_SENDING,
        payload: false,
      });
      return ErrorCode.Generic;
    }
  };
}

export function updateAddress(newAddress: AddressData, activeCartId: string, temporary?: boolean): ThunkResult<void> {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_ADDRESS,
      payload: newAddress,
    });

    dispatch({
      type: SET_IS_SENDING,
      payload: SendingType.ADDRESS,
    });

    const finishSending = () =>
      dispatch({
        type: SET_IS_SENDING,
        payload: false,
      });

    sendAddressData(activeCartId, newAddress.id, temporary).then(finishSending).catch(finishSending);
  };
}

export function updateUnloadingPoint(unloadingPoint: UnloadingPointData) {
  return {
    type: UPDATE_UNLOADING_POINT,
    payload: unloadingPoint,
  };
}
